import { API, Competition, Endpoints, CompetitionAPI, TLang } from '@brg/api';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { STATUSES } from '_competition/const';
import { COMPETITION_TABS } from '_competition/pages/CompetitionsAdmin/const';
import { FolderT } from 'shared/pages/designer/components/tabs/const';

export type CompetitionAdminGetListParams = Record<string, unknown>;

const parseListResponse = async (response: Response): Promise<Competition<'Global'>[]> => {
  const json = await response.json();
  const data = json?.data as Competition<'Global'>[] | undefined;
  return Array.isArray(data) ? data : [];
};

export const getList = createAsyncThunk(
  'competitionAdmin/getList',
  async (params: CompetitionAdminGetListParams, { rejectWithValue }) => {
    try {
      const res = await CompetitionAPI.GetList(params as any);
      const list = await parseListResponse(res);

      if (params.search) {
        return list.filter((competition) => {
          //@ts-ignore
         return competition.name?.some(({name}) => name?.toLowerCase().includes(params?.search?.toLowerCase()));
        });
      }
      return list.reverse();
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchFolders = createAsyncThunk(
  'competitionAdmin/fetchFolders',
  async (designerId: string, { rejectWithValue }) => {  
      const res = await API.request()
      .endpoint(Endpoints.folders)
      .url('/api/Folder/GetFoldersByAccId')
      .addQuery('accountId', designerId)
      .addQuery('entityType', 'competition')
      .get()
      .send()
      return { data: await res.json() };
  },
);

export const GetList = getList;

export const getBriefsList = createAsyncThunk(
  'competitionAdmin/getBriefsList',
  async (params: CompetitionAdminGetListParams = {}, { rejectWithValue }) => {
    try {
      const [regularRes, automaticRes] = await Promise.all([
        CompetitionAPI.GetList({ isAutomatic: false, ...params } as any),
        CompetitionAPI.GetList({ isAutomatic: true, ...params } as any),
      ]);
      const [regular, automatic] = await Promise.all([
        parseListResponse(regularRes),
        parseListResponse(automaticRes),
      ]);
      return [...regular.reverse(), ...automatic.reverse()];
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export interface CompetitionAdminSliceState {
  isLoading: boolean;
  allCompetitions: Competition<'Global'>[];
  currentStatusForTab: COMPETITION_TABS;
  selectedCompetitions: string[];
  isTimelineVisible: boolean;
  isCreated: boolean;
  createdType: 'rating' | 'folder' | 'regular' | '';
  folders: FolderT[];
}

const initialState: CompetitionAdminSliceState = {
  isLoading: false,
  allCompetitions: [],
  selectedCompetitions: [],
  currentStatusForTab: COMPETITION_TABS.all,
  isTimelineVisible: false,
  isCreated: false,
  createdType: '',
  folders: [],
};

export const CompetitionAdminSlice = createSlice({
  name: 'competitionAdmin',
  initialState,
  reducers: {
    setCurrentStatusForTab: (state, action: PayloadAction<COMPETITION_TABS>) => {
      state.currentStatusForTab = action.payload;
    },
    setSelectedCompetitions: (state, action: PayloadAction<string[]>) => {
      state.selectedCompetitions = action.payload;
    },
    toggleSelectedCompetition: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.selectedCompetitions.indexOf(id);
      if (idx === -1) {
        state.selectedCompetitions.push(id);
      } else {
        state.selectedCompetitions.splice(idx, 1);
      }
    },
    clearSelectedCompetitions: (state) => {
      state.selectedCompetitions = [];
    },
    clearCompetitions: (state) => {
      state.allCompetitions = [];
      state.isLoading = false;
    },
    toggleTimeline: (state) => {
      state.isTimelineVisible = !state.isTimelineVisible;
    },
    setFolders: (state, action: PayloadAction<FolderT[]>) => {
      state.folders = action.payload ?? [];
    },
    clearFolders: (state) => {
      state.folders = [];
    },
    clearCurrentStatusForTab: (state) => {
      state.currentStatusForTab = COMPETITION_TABS.all;
    },
    setIsCreated: (state, action: PayloadAction<{ isCreated: boolean, createdType: 'rating' | 'folder' | 'regular' | '' }>) => {
      state.isCreated = action.payload?.isCreated ?? false;
      state.createdType = action.payload?.createdType ?? '';
    },
    updateFolderAfterMove: (state, action: PayloadAction<{ folderId: string, entityIds: string[] }>) => {
      const { folderId, entityIds } = action.payload;
      state.folders = state.folders.map((folder) => {
        if (folder.id === folderId) {
          return { ...folder, entityIds };
        }
        return folder;
      });
      state.selectedCompetitions = []
    },
    updateFolderAfterSave: (state, action: PayloadAction<{ folderId: string, name: TLang[] }>) => {
      const { folderId, name } = action.payload;
      state.folders = state.folders.map((folder) => {
        if (folder.id === folderId) {
          return { ...folder, name: JSON.stringify(name) };
        }
        return folder;
      });
    },
    updateCompetitions: (state, action: PayloadAction<{ selectedCompetitions: string[], status: number }>) => {
      const { selectedCompetitions, status } = action.payload;
      const isDeleted = status === STATUSES.Deleted;
      const isStatusTab = state.currentStatusForTab !== COMPETITION_TABS.all;

      if (isDeleted && isStatusTab) {
        state.allCompetitions = state.allCompetitions.filter((competition) => !selectedCompetitions.includes(competition.competitionId ?? ''));
      } else {
        state.allCompetitions = state.allCompetitions.map((competition) => {
          if (selectedCompetitions.includes(competition.competitionId ?? '')) {
            return { ...competition, status: status };
          }
          return competition;
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allCompetitions = action.payload;
      })
      .addCase(getList.rejected, (state) => {
        state.isLoading = false;
        state.allCompetitions = [];
      })
      .addCase(getBriefsList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBriefsList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allCompetitions = action.payload;
      })
      .addCase(getBriefsList.rejected, (state) => {
        state.isLoading = false;
        state.allCompetitions = [];
      })
      .addCase(fetchFolders.pending, (state) => {
        state.folders = [];
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.folders = action.payload.data;
      })
      .addCase(fetchFolders.rejected, (state) => {
        state.folders = [];
      });
  },
});

export const CompetitionAdminActions = {
  ...CompetitionAdminSlice.actions,
  getList,
  GetList,
  getBriefsList,
  fetchFolders,
};

export const CompetitionAdminSelector = (store: {
  competitionAdmin: CompetitionAdminSliceState;
}): CompetitionAdminSliceState => store.competitionAdmin;

export default CompetitionAdminSlice;
import { API, Competition, Endpoints, CompetitionAPI, TLang } from '@brg/api';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { STATUSES } from '_competition/const';
import { COMPETITION_TABS } from '_competition/pages/CompetitionsAdmin/const';
import { FolderT } from 'shared/pages/designer/components/tabs/const';

export type CompetitionAdminGetListParams = Record<string, unknown>;

const parseListResponse = async (response: Response): Promise<Competition<'Global'>[]> => {
  const json = await response.json();
  const data = json?.data as Competition<'Global'>[] | undefined;
  return Array.isArray(data) ? data : [];
};

export const getList = createAsyncThunk(
  'competitionAdmin/getList',
  async (params: CompetitionAdminGetListParams, { rejectWithValue }) => {
    try {
      const res = await CompetitionAPI.GetList(params as any);
      const list = await parseListResponse(res);

      if (params.search) {
        return list.filter((competition) => {
          //@ts-ignore
         return competition.name?.some(({name}) => name?.toLowerCase().includes(params?.search?.toLowerCase()));
        });
      }
      return list.reverse();
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchFolders = createAsyncThunk(
  'competitionAdmin/fetchFolders',
  async (designerId: string, { rejectWithValue }) => {  
      const res = await API.request()
      .endpoint(Endpoints.folders)
      .url('/api/Folder/GetFoldersByAccId')
      .addQuery('accountId', designerId)
      .addQuery('entityType', 'competition')
      .get()
      .send()
      return { data: await res.json() };
  },
);

export const GetList = getList;

export const getBriefsList = createAsyncThunk(
  'competitionAdmin/getBriefsList',
  async (params: CompetitionAdminGetListParams = {}, { rejectWithValue }) => {
    try {
      const [regularRes, automaticRes] = await Promise.all([
        CompetitionAPI.GetList({ isAutomatic: false, ...params } as any),
        CompetitionAPI.GetList({ isAutomatic: true, ...params } as any),
      ]);
      const [regular, automatic] = await Promise.all([
        parseListResponse(regularRes),
        parseListResponse(automaticRes),
      ]);
      return [...regular.reverse(), ...automatic.reverse()];
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export interface CompetitionAdminSliceState {
  isLoading: boolean;
  allCompetitions: Competition<'Global'>[];
  currentStatusForTab: COMPETITION_TABS;
  selectedCompetitions: string[];
  isTimelineVisible: boolean;
  isCreated: boolean;
  createdType: 'rating' | 'folder' | 'regular' | '';
  folders: FolderT[];
}

const initialState: CompetitionAdminSliceState = {
  isLoading: false,
  allCompetitions: [],
  selectedCompetitions: [],
  currentStatusForTab: COMPETITION_TABS.all,
  isTimelineVisible: false,
  isCreated: false,
  createdType: '',
  folders: [],
};

export const CompetitionAdminSlice = createSlice({
  name: 'competitionAdmin',
  initialState,
  reducers: {
    setCurrentStatusForTab: (state, action: PayloadAction<COMPETITION_TABS>) => {
      state.currentStatusForTab = action.payload;
    },
    setSelectedCompetitions: (state, action: PayloadAction<string[]>) => {
      state.selectedCompetitions = action.payload;
    },
    toggleSelectedCompetition: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.selectedCompetitions.indexOf(id);
      if (idx === -1) {
        state.selectedCompetitions.push(id);
      } else {
        state.selectedCompetitions.splice(idx, 1);
      }
    },
    clearSelectedCompetitions: (state) => {
      state.selectedCompetitions = [];
    },
    clearCompetitions: (state) => {
      state.allCompetitions = [];
      state.isLoading = false;
    },
    toggleTimeline: (state) => {
      state.isTimelineVisible = !state.isTimelineVisible;
    },
    setFolders: (state, action: PayloadAction<FolderT[]>) => {
      state.folders = action.payload ?? [];
    },
    clearFolders: (state) => {
      state.folders = [];
    },
    clearCurrentStatusForTab: (state) => {
      state.currentStatusForTab = COMPETITION_TABS.all;
    },
    setIsCreated: (state, action: PayloadAction<{ isCreated: boolean, createdType: 'rating' | 'folder' | 'regular' | '' }>) => {
      state.isCreated = action.payload?.isCreated ?? false;
      state.createdType = action.payload?.createdType ?? '';
    },
    updateFolderAfterMove: (state, action: PayloadAction<{ folderId: string, entityIds: string[] }>) => {
      const { folderId, entityIds } = action.payload;
      state.folders = state.folders.map((folder) => {
        if (folder.id === folderId) {
          return { ...folder, entityIds };
        }
        return folder;
      });
      state.selectedCompetitions = []
    },
    updateFolderAfterSave: (state, action: PayloadAction<{ folderId: string, name: TLang[] }>) => {
      const { folderId, name } = action.payload;
      state.folders = state.folders.map((folder) => {
        if (folder.id === folderId) {
          return { ...folder, name: JSON.stringify(name) };
        }
        return folder;
      });
    },
    updateCompetitions: (state, action: PayloadAction<{ selectedCompetitions: string[], status: number }>) => {
      const { selectedCompetitions, status } = action.payload;
      const isDeleted = status === STATUSES.Deleted;
      const isStatusTab = state.currentStatusForTab !== COMPETITION_TABS.all;

      if (isDeleted && isStatusTab) {
        state.allCompetitions = state.allCompetitions.filter((competition) => !selectedCompetitions.includes(competition.competitionId ?? ''));
      } else {
        state.allCompetitions = state.allCompetitions.map((competition) => {
          if (selectedCompetitions.includes(competition.competitionId ?? '')) {
            return { ...competition, status: status };
          }
          return competition;
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allCompetitions = action.payload;
      })
      .addCase(getList.rejected, (state) => {
        state.isLoading = false;
        state.allCompetitions = [];
      })
      .addCase(getBriefsList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBriefsList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allCompetitions = action.payload;
      })
      .addCase(getBriefsList.rejected, (state) => {
        state.isLoading = false;
        state.allCompetitions = [];
      })
      .addCase(fetchFolders.pending, (state) => {
        state.folders = [];
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.folders = action.payload.data;
      })
      .addCase(fetchFolders.rejected, (state) => {
        state.folders = [];
      });
  },
});

export const CompetitionAdminActions = {
  ...CompetitionAdminSlice.actions,
  getList,
  GetList,
  getBriefsList,
  fetchFolders,
};

export const CompetitionAdminSelector = (store: {
  competitionAdmin: CompetitionAdminSliceState;
}): CompetitionAdminSliceState => store.competitionAdmin;

export default CompetitionAdminSlice;
